import React, { useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
} from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import {
  GenericMapAgreement,
  GenericMapAgreementLine,
  GenericMapPlace,
  GeoJsonObject,
  ISmartMapFilterType,
  SmartMapGridTypes,
} from '../../../models/GenericMapModels.model';
import ColumnMenu from '../../shared/ColumnMenu';
import { getColumns } from '../../../utils/genericMapUtils';

export interface ISmartMapGridProps {
  gridType: SmartMapGridTypes;
  genericMapRoutes: any;
  agreements: GenericMapAgreement[];
  places: GenericMapPlace[];
  bins: GenericMapAgreementLine[];
  setSelectedElements: (selectedElements: SmartMapGridElement[]) => void;
  selectedRoutes: GeoJsonObject;
  filterType: ISmartMapFilterType;
}

export interface SmartMapGridElement {
  name?: string;
  routeNames?: string[];
  agreementId?: number;
  address?: string;
  unit?: string;
  type?: string;
  paSystem?: string;
  estate?: string;
  agreementName?: string;
  fraction?: string;
  id: string | number;
}

const SmartMapGrid: React.FC<ISmartMapGridProps> = ({
  agreements,
  gridType,
  places,
  bins,
  setSelectedElements,
  selectedRoutes,
  filterType,
}) => {
  const dataStateObject =
    gridType === SmartMapGridTypes.BINS_GRID
      ? {
          sort: [{ dir: 'asc', field: 'name' }],
          take: 50,
          skip: 0,
          group: [{ field: 'agreementName' }],
        }
      : {
          sort: [{ dir: 'asc', field: 'address' }],
          take: 50,
          skip: 0,
        };
  const [dataState, setDataState] = useState(dataStateObject);

  const dataStateChange = (event: any) => {
    setDataState(event.data);
  };

  const [selectedItems, setSelectedItems] = useState<SmartMapGridElement[]>([]);

  const getSelectedRouteIds = (): string[] => {
    const selectedRouteIds: string[] = [];
    selectedRoutes.features?.forEach((route) => {
      const routeId = route.properties.routeId;
      if (!selectedRouteIds.includes(routeId)) {
        selectedRouteIds.push(routeId);
      }
    });
    return selectedRouteIds;
  };

  const selectedRouteIds = getSelectedRouteIds();

  const getGridData = (): Array<SmartMapGridElement> => {
    const gridData: Array<SmartMapGridElement> = [];
    if (gridType === SmartMapGridTypes.AGREEMENTS_GRID && agreements.length) {
      agreements.forEach((agreement) => {
        if (
          filterType !== ISmartMapFilterType.ROUTE ||
          (filterType === ISmartMapFilterType.ROUTE &&
            agreement.routeIds.some((routeId) =>
              selectedRouteIds.includes(routeId)
            ))
        ) {
          const gridElement: SmartMapGridElement = {
            type: agreement.agreementType,
            address: agreement.agreementAddress,
            estate: agreement.agreementEstate,
            agreementId: agreement.agreementId,
            id: agreement.agreementId,
            routeNames: agreement.routeNames,
          };
          gridData.push(gridElement);
        }
      });
    } else if (gridType === SmartMapGridTypes.PLACES_GRID && places.length) {
      places.forEach((place) => {
        if (
          filterType !== ISmartMapFilterType.ROUTE ||
          (filterType === ISmartMapFilterType.ROUTE &&
            place.routeIds.some((routeId) =>
              selectedRouteIds.includes(routeId)
            ))
        ) {
          const gridElement: SmartMapGridElement = {
            address: place.placeName,
            type: place.placeType,
            id: place.placeNumber,
          };
          gridData.push(gridElement);
        }
      });
    } else if (gridType === SmartMapGridTypes.BINS_GRID && bins.length) {
      bins.forEach((bin) => {
        if (
          filterType !== ISmartMapFilterType.ROUTE ||
          (filterType === ISmartMapFilterType.ROUTE &&
            bin.routeIds.some((routeId) => selectedRouteIds.includes(routeId)))
        ) {
          const gridElement: SmartMapGridElement = {
            name: bin.unitName,
            fraction: bin.fractionName,
            agreementName: bin.agreementAddress,
            id: bin.agreementLineId,
          };
          gridData.push(gridElement);
        }
      });
    }
    return gridData;
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: any) => {
    return GridColumnMenuFilter.active(field, dataState.filter);
  };

  const handleSelectionChange = (item: SmartMapGridElement) => {
    setSelectedItems((prevSelected) => {
      const exists = prevSelected.find((selected) => selected.id === item.id);
      if (exists) {
        const selectedElements = prevSelected.filter(
          (selected) => selected.id !== item.id
        );
        setSelectedElements(selectedElements);
        return selectedElements;
      }
      const selectedElements = [...prevSelected, item];
      setSelectedElements(selectedElements);
      return selectedElements;
    });
  };

  const CheckboxCell = (props: GridCellProps) => {
    const item = props.dataItem as any;
    if (item.field !== undefined) {
      return <td />;
    }

    return (
      <td>
        <input
          type="checkbox"
          checked={selectedItems.some((selected) => selected.id === item.id)}
          onChange={() => handleSelectionChange(item)}
        />
      </td>
    );
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelectedItems = [
        ...selectedItems,
        ...visibleDataItems.filter(
          (item) => !selectedItems.some((sel) => sel.id === item.id)
        ),
      ];
      setSelectedItems(newSelectedItems);
      setSelectedElements(newSelectedItems);
    } else {
      const remaining = selectedItems.filter(
        (sel) => !visibleDataItems.some((item) => item.id === sel.id)
      );
      setSelectedItems(remaining);
      setSelectedElements(remaining);
    }
  };

  const flattenGroupedData = (data: any[]): SmartMapGridElement[] => {
    const result: SmartMapGridElement[] = [];

    const extract = (items: any[]) => {
      items.forEach((item) => {
        if (item.items) {
          extract(item.items); // recurse into groups
        } else {
          result.push(item);
        }
      });
    };

    extract(data);
    return result;
  };

  const gridData = getGridData();
  const result = process(gridData, dataState);

  const visibleDataItems =
    gridType === SmartMapGridTypes.BINS_GRID
      ? flattenGroupedData(result.data)
      : (result.data as SmartMapGridElement[]);

  const columns = getColumns(gridType);
  return (
    <>
      <Grid
        {...dataState}
        className={`smart-map-grid`}
        data={result}
        selectedField="selected"
        pageable
        resizable
        sortable
        pageSize={50}
        groupable={gridType === SmartMapGridTypes.BINS_GRID}
        onDataStateChange={dataStateChange}
      >
        <GridColumn
          field="selected"
          width="50px"
          headerCell={() => {
            const ref = React.useRef<HTMLInputElement>(null);

            React.useEffect(() => {
              if (ref.current) {
                const someSelected = visibleDataItems.some((item) =>
                  selectedItems.some((selected) => selected.id === item.id)
                );
                const allSelected =
                  visibleDataItems.length > 0 &&
                  visibleDataItems.every((item) =>
                    selectedItems.some((selected) => selected.id === item.id)
                  );

                ref.current.indeterminate = someSelected && !allSelected;
              }
            }, [visibleDataItems, selectedItems]);

            const allChecked =
              visibleDataItems.length > 0 &&
              visibleDataItems.every((item) =>
                selectedItems.some((selected) => selected.id === item.id)
              );

            return (
              <input
                ref={ref}
                type="checkbox"
                checked={allChecked}
                onChange={handleSelectAllChange}
                style={{ marginLeft: '8px' }}
              />
            );
          }}
          cell={CheckboxCell}
        />
        {columns.map((column) => {
          return (
            <GridColumn
              {...columnProps(column.field)}
              key={column.field}
              title={column.title}
              groupable={column.groupable}
              width={column.width}
            />
          );
        })}
      </Grid>
    </>
  );
};

export default SmartMapGrid;
